import type { MessageDescriptor } from "@lingui/core";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { humanizeToolName } from "@orbin/core";

const TOOL_LABELS: Record<string, MessageDescriptor> = {
  computer_observe: msg`Look at the screen`,
  computer_act: msg`Operate the computer`,
  browser_navigate: msg`Open a page`,
  browser_snapshot: msg`Read the page`,
  browser_act: msg`Use the page`,
  list_files: msg`List files`,
  read_file: msg`Read a file`,
  write_file: msg`Write a file`,
  attach_file: msg`Attach a file`,
  shell: msg`Terminal`,
  user_computer_shell: msg`Terminal on your computer`,
  open_path: msg`Open a file`,
  launch_app: msg`Open an app`,
  request_takeover: msg`Ask you to take over`,
  ask_user: msg`Ask you`,
  connect_app: msg`Connect an app`,
  message_user: msg`Message you`,
  request_secret: msg`Request a secret`,
  generate_secret: msg`Generate a secret`,
  list_secrets: msg`List secrets`,
  secret_request: msg`Secret request`,
  forget_secret: msg`Forget a secret`,
  render_plot: msg`Draw a chart`,
  generate_image: msg`Generate an image`,
  generate_video: msg`Generate a video`,
  add_mcp_server: msg`Add a connection`,
  remember: msg`Remember`,
  save_shared_memory: msg`Save to shared memory`,
  save_memory: msg`Save to memory`,
  recall_memory: msg`Recall memory`,
  forget_memory: msg`Forget memory`,
  web_search: msg`Search the web`,
  web_fetch: msg`Read a web page`,
  search_history: msg`Search history`,
  read_history: msg`Read history`,
  schedule_create: msg`Create a routine`,
  schedule_list: msg`List routines`,
  schedule_update: msg`Update a routine`,
  schedule_cancel: msg`Cancel a routine`,
  call_user: msg`Call you`,
  end_call: msg`End the call`,
  run_subagent: msg`Delegate to a helper`,
  message_bot: msg`Message a colleague`,
  spawn_bot: msg`Create a colleague`,
  cloud_agent_launch: msg`Start a cloud agent`,
  cloud_agent_status: msg`Check a cloud agent`,
  cloud_agent_reply: msg`Reply to a cloud agent`,
  cloud_agent_cancel: msg`Cancel a cloud agent`,
  request_purchase: msg`Request a purchase`,
  purchase_status: msg`Check a purchase`,
  paid_request: msg`Paid request`,
  task_catalog: msg`Task catalog`,
  scratchpad_list: msg`List notes`,
  scratchpad_add: msg`Add a note`,
  scratchpad_update: msg`Update a note`,
  scratchpad_complete: msg`Complete a note`,
  scratchpad_remove: msg`Remove a note`,
  board_tickets: msg`List tickets`,
  ticket_get: msg`Open a ticket`,
  ticket_create: msg`Create a ticket`,
  ticket_update: msg`Update a ticket`,
  ticket_comment: msg`Comment on a ticket`,
  skill_read: msg`Read a skill`,
  skill_create: msg`Create a skill`,
  skill_update: msg`Update a skill`,
  skill_delete: msg`Delete a skill`,
  propose_instructions_update: msg`Propose new instructions`,
  create_space: msg`Create a space`,
  update_bot: msg`Update a colleague`,
  list_fleet: msg`List colleagues`,
  create_group: msg`Create a group`,
  update_group: msg`Update a group`,
  organize_bot: msg`Organize the sidebar`,
  set_child_instructions: msg`Set a colleague's instructions`,
  set_child_bot_model: msg`Set a colleague's model`,
  create_child_routine: msg`Create a colleague's routine`,
  archive_bot: msg`Archive a colleague`,
  handoff_to_bot: msg`Hand off to a colleague`,
  connect_agent: msg`Connect an agent`,
  respond_agent_connection: msg`Answer an agent connection`,
  message_agent: msg`Message an agent`,
};

const BY_LABEL = new Map(Object.keys(TOOL_LABELS).map((name) => [humanizeToolName(name), name]));

/** Localized tool name; accepts a raw tool name or the humanized label stored on a step. */
export function toolLabel(nameOrLabel: string): string {
  // Raw tool names are lowercase snake_case ("remember", "web_fetch"); stored step labels are already humanized.
  const fallback = /^[a-z][a-z0-9_]*$/.test(nameOrLabel)
    ? humanizeToolName(nameOrLabel)
    : nameOrLabel;
  if (!i18n.locale) return fallback;
  const name = TOOL_LABELS[nameOrLabel] ? nameOrLabel : BY_LABEL.get(nameOrLabel);
  const descriptor = name ? TOOL_LABELS[name] : undefined;
  return descriptor ? i18n._(descriptor) : fallback;
}

const ACTIVITY_LINES: ReadonlyArray<[RegExp, (m: RegExpMatchArray) => string]> = [
  [
    /^Running on your computer: (.*)$/s,
    (m) => i18n._(msg`Running on your computer: ${m[1] ?? ""}`),
  ],
  [/^Running: (.*)$/s, (m) => i18n._(msg`Running: ${m[1] ?? ""}`)],
  [/^Running a script$/, () => i18n._(msg`Running a script`)],
  [/^Running a command$/, () => i18n._(msg`Running a command`)],
  [/^Running a script on your computer$/, () => i18n._(msg`Running a script on your computer`)],
  [/^Running a command on your computer$/, () => i18n._(msg`Running a command on your computer`)],
  [/^Reading (.+) from (.+)$/s, (m) => i18n._(msg`Reading ${m[1] ?? ""} from ${m[2] ?? ""}`)],
  [/^Reading skill: (.*)$/s, (m) => i18n._(msg`Reading skill: ${m[1] ?? ""}`)],
  [/^Reading page: (.*)$/s, (m) => i18n._(msg`Reading page: ${m[1] ?? ""}`)],
  [/^Reading the page$/, () => i18n._(msg`Reading the page`)],
  [/^Reading (.*)$/s, (m) => i18n._(msg`Reading ${m[1] ?? ""}`)],
  [/^Writing (.*)$/s, (m) => i18n._(msg`Writing ${m[1] ?? ""}`)],
  [/^Listing (.*)$/s, (m) => i18n._(msg`Listing ${m[1] ?? ""}`)],
  [/^Attaching (.*)$/s, (m) => i18n._(msg`Attaching ${m[1] ?? ""}`)],
  [/^Opening page: (.*)$/s, (m) => i18n._(msg`Opening page: ${m[1] ?? ""}`)],
  [/^Opening (.*)$/s, (m) => i18n._(msg`Opening ${m[1] ?? ""}`)],
  [/^Searching the web: (.*)$/s, (m) => i18n._(msg`Searching the web: ${m[1] ?? ""}`)],
  [/^Rendering a chart$/, () => i18n._(msg`Rendering a chart`)],
  [/^Looking at the screen$/, () => i18n._(msg`Looking at the screen`)],
  [/^Using the page$/, () => i18n._(msg`Using the page`)],
  [/^Operating the computer$/, () => i18n._(msg`Operating the computer`)],
  [/^Saving a note to memory$/, () => i18n._(msg`Saving a note to memory`)],
  [/^Delegating to helper: (.*)$/s, (m) => i18n._(msg`Delegating to helper: ${m[1] ?? ""}`)],
  [/^Using (.*)$/s, (m) => i18n._(msg`Using ${m[1] ?? ""}`)],
];

/**
 * Localizes a tool status line the runtime streams. Any other line, such as the model's own
 * narration in whatever language it wrote, has no label here and returns undefined.
 */
export function localizeActivityLine(line: string): string | undefined {
  if (!i18n.locale) return undefined;
  for (const [pattern, render] of ACTIVITY_LINES) {
    const match = line.match(pattern);
    if (match) return render(match);
  }
  return undefined;
}
